// Wind-speed → colour using Skysight's DISPLAYED legend palette (the pastel
// rainbow shown on its map + legend), captured from
// docs/skysight-overlay-styles/surface-wind.json `legend` (value 0..12 m/s).
// This is the colour set Skysight actually renders — the saturated
// `activePaletteKey` set rendered too green on our basemap. The same rainbow is
// stretched to each product's max via colorForSpeed(speed, max).

// 0 m/s → 12 m/s (ascending), [r,g,b].
export const WIND_LEGEND_STOPS = [
  [96, 96, 180],   // 0
  [96, 109, 246],  // 1
  [96, 189, 246],  // 2
  [97, 239, 225],  // 3
  [99, 215, 150],  // 4
  [131, 211, 103], // 5
  [208, 235, 98],  // 6
  [246, 231, 96],  // 7
  [246, 202, 96],  // 8
  [246, 157, 96],  // 9
  [246, 106, 96],  // 10
  [204, 96, 130],  // 11
  [151, 96, 173],  // >12
];

// Parse a Skysight palette key ("<n>:<RRGGBBAA><RRGGBBAA>…") into a colour
// list [[r,g,b,a], …]. Alpha matters: the scalar (convergence/ridge) palettes
// carry fully transparent neutral bands straddling zero.
export function parsePaletteKey(key) {
  const hex = key.slice(key.indexOf(':') + 1);
  const colors = [];
  for (let i = 0; i + 8 <= hex.length; i += 8) {
    colors.push([
      parseInt(hex.slice(i, i + 2), 16),
      parseInt(hex.slice(i + 2, i + 4), 16),
      parseInt(hex.slice(i + 4, i + 6), 16),
      parseInt(hex.slice(i + 6, i + 8), 16),
    ]);
  }
  return colors;
}

// Discrete isoband colour, matching Skysight's scalar contour shader: band
// index = floor((value − min) / step), values ≥ max take the last colour
// (overflow). NO interpolation — Skysight renders scalar fills banded.
// Returns [r,g,b,a].
export function scalarBandColor(value, { min, max, step }, colors) {
  if (value >= max) return colors[colors.length - 1];
  const band = Math.floor((value - min) / step + 1e-4);
  return colors[Math.max(0, Math.min(colors.length - 2, band))];
}

// speedMs, max (m/s at the top of the scale) → [r,g,b]. Linear interpolation
// across the ramp, normalized over 0..max, clamped at both ends.
export function colorForSpeed(speedMs, max) {
  const stops = WIND_LEGEND_STOPS;
  const t = Math.max(0, Math.min(1, speedMs / max));
  const pos = t * (stops.length - 1);
  const i = Math.floor(pos);
  if (i >= stops.length - 1) return stops[stops.length - 1].slice();
  const f = pos - i;
  const a = stops[i], b = stops[i + 1];
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}
